import { numberColumn } from '@/shared/grids/columns/numberColumn';
import { RecordsStatus, RecurringTaskStatus, TaskStatus } from '@/shared/types/enum';
import { recurringTasksFilterUrl, smartTasksFilterUrl, tasksFilterUrl } from '@/shared/utils/url';

import { TaskView } from '@prisma/client';

const width = 150;

type TaskCountColumnOptions = {
  uid?: string;
  linkIdField: keyof Pick<TaskView, 'clientId' | 'serviceId'>;
  visible?: boolean;
};

export const taskCountColumn = <T extends { taskCount: number | null }>(options: TaskCountColumnOptions) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'taskCount',
    header: 'Tasks',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}' }),
    positiveSuffix: pluralSuffix<T>(' task', 'taskCount'),
    zeroText: 'No tasks',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const scheduledTaskCountColumn = <T extends { scheduledTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'scheduledTaskCount',
    header: 'Scheduled',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.Scheduled }),
    positiveSuffix: ' scheduled',
    zeroText: 'None scheduled',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const unscheduledTaskCountColumn = <T extends { unscheduledTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'unscheduledTaskCount',
    header: 'Unscheduled',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.Unscheduled }),
    positiveSuffix: ' to schedule',
    zeroText: 'None to schedule',
    allowFiltering: false,
    visible: options.visible ?? true,
  });

export const recordsRequiredTaskCountColumn = <T extends { recordsRequiredTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'recordsRequiredTaskCount',
    header: 'Records Required',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', recordsStatus: RecordsStatus.Required }),
    positiveSuffix: ' required',
    zeroText: 'None required',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const recordsReceivedTaskCountColumn = <T extends { recordsReceivedTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'recordsReceivedTaskCount',
    header: 'Records Received',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', recordsStatus: RecordsStatus.Received }),
    positiveSuffix: ' received',
    zeroText: 'None received',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const startDueTaskCountColumn = <T extends { startDueTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'startDueTaskCount',
    header: 'Start Due',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.StartDue }),
    positiveClass: 'text-warning',
    positiveSuffix: ' due to start',
    zeroText: 'None to start',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const startedTaskCountColumn = <T extends { startedTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'startedTaskCount',
    header: 'Started',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.Started }),
    positiveClass: 'text-success',
    positiveSuffix: ' started',
    zeroText: 'None started',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const completionDueTaskCountColumn = <T extends { completionDueTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'completionDueTaskCount',
    header: 'Completion Due',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.CompletionDue }),
    positiveClass: 'text-warning',
    positiveSuffix: ' due to complete',
    zeroText: 'None to complete',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const completedTaskCountColumn = <T extends { completedTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'completedTaskCount',
    header: 'Completed (All Time)',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.Completed }),
    positiveClass: 'text-success',
    positiveSuffix: ' completed',
    zeroText: 'None completed',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const completed30DaysTaskCountColumn = <T extends { completed30DaysTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'completed30DaysTaskCount',
    header: 'Completed (30 Days)',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', preset: 'completed30Days' }),
    positiveClass: 'text-success',
    positiveSuffix: ' completed',
    zeroText: 'None completed',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const deadlineDateDueTaskCountColumn = <T extends { deadlineDateDueTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'deadlineDateDueTaskCount',
    header: 'Deadline Due',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', status: TaskStatus.DeadlineDue }),
    positiveClass: 'text-danger',
    positiveSuffix: ' due',
    zeroText: 'None due',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const uncompletedTaskCountColumn = <T extends { uncompletedTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'uncompletedTaskCount',
    header: 'Tasks',
    linkUrl: tasksFilterUrl({ [options.linkIdField]: '{id}', preset: 'uncompleted' }),
    positiveSuffix: pluralSuffix<T>(' task', 'uncompletedTaskCount'),
    zeroText: 'No tasks',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const recurringTaskCountColumn = <T extends { recurringTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'recurringTaskCount',
    header: 'Recurring',
    linkUrl: recurringTasksFilterUrl({ [options.linkIdField]: '{id}' }),
    positiveSuffix: ' recurring',
    zeroText: 'No recurring',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

export const scheduledRecurringTaskCountColumn = <T extends { scheduledRecurringTaskCount: number | null }>(
  options: TaskCountColumnOptions,
) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'scheduledRecurringTaskCount',
    header: 'Recurring',
    linkUrl: recurringTasksFilterUrl({ [options.linkIdField]: '{id}', status: RecurringTaskStatus.Scheduled }),
    positiveSuffix: ' recurring',
    zeroText: 'No recurring',
    allowFiltering: false,
    width,
    visible: options.visible ?? true,
  });

// export const completedRecurringTaskCountColumn = <T extends { completedRecurringTaskCount: number | null }>(
//   options: TaskCountColumnOptions
// ) =>
//   numberColumn<T>({
//     uid: options.uid,
//     field: 'completedRecurringTaskCount',
//     header: 'Recurring',
//     linkUrl: recurringTasksFilterUrl({ [options.linkIdField]: '{id}', status: RecurringTaskStatus.Completed }),
//     positiveSuffix: ' completed',
//     zeroText: 'None completed',
//     allowFiltering: false,
//     width,
//     visible: options.visible ?? true,
//   });

export const smartTaskCountColumn = <T extends { smartTaskCount: number | null }>(options: TaskCountColumnOptions) =>
  numberColumn<T>({
    uid: options.uid,
    field: 'smartTaskCount',
    header: 'Smart Tasks',
    linkUrl: smartTasksFilterUrl({ [options.linkIdField]: '{id}' }),
    positiveSuffix: pluralSuffix<T>(' Smart Task', 'smartTaskCount'),
    zeroText: 'No Smart Tasks',
    allowFiltering: false,
    width,
    visible: options.visible ?? false,
  });

const pluralSuffix = <T>(suffix: string, field: keyof T & string) => `${suffix}\${if(${field} > 1)}s\${/if}`;
